import React from 'react'

import { BugIcon, LifeBuoyIcon, SparklesIcon } from 'lucide-react'

import { Stack, Typography, useTheme } from '@mui/material'

import { useColorMode } from 'hooks'
import { SUPPORT_REQUEST_TYPES } from 'constants/statuses'
// Sibling path, not the `components` barrel: this file is exported FROM that barrel, so
// importing back through it would be a cycle (the same reason TagCapabilityBadges reaches
// for `../ComingSoonNote` directly).
import { STATUS_TOKEN_H, statusTone } from '../StatusChip/StatusChip'

export type SupportRequestTypeKey = keyof typeof SUPPORT_REQUEST_TYPES

/**
 * ONE badge for a support request's TYPE, rendered identically everywhere it appears (§36):
 * on the request cards in the list, and as the toggle option in the Filter drawer's Type
 * facet. Those are the two hand-rolls §36 exists to prevent — a card badge and a filter
 * chip that merely look alike drift the moment a glyph or a hue changes on one of them.
 *
 * ## Why type is a BADGE while status stays a PILL
 *
 * They answer different questions and should not read as the same kind of token. Status is a
 * LIFECYCLE state — it moves (open → in progress → resolved), which is exactly what the
 * status pill means everywhere else in the app. Type is a fixed CLASSIFICATION chosen at
 * intake and never changes. Giving both the same pill made the card's two chips read as two
 * states of one thing. The badge treatment here is the one already established for content
 * labels on the What's new page (`AnnouncementCard`): rounded rect, leading glyph, a single
 * tone driving both the text and a 10% fill.
 *
 * ## Glyph and tone per type
 *
 * Colours are NOT new — each is the hue already authored in `SUPPORT_REQUEST_TYPES`, read
 * through `statusTone` so dark mode lightens the text the same way every other status token
 * does. Only the glyphs are added:
 *
 *   - `support` → life buoy. Already this app's support glyph: the empty state on this very
 *     page and the sidebar's support entry both use it, so the badge matches the surface it
 *     sits on. Blue.
 *   - `bug` → bug. Red, which is the severity read the colour already carried.
 *   - `feature` → sparkles. Deliberately the SAME glyph `AnnouncementCard` uses for a
 *     `feature` announcement: a feature request and a shipped feature are the same thing at
 *     two ends of one pipeline, and a reader who sees their request ship should recognise the
 *     mark. Purple, which sits in the brand family.
 */
const TYPE_ICON: Record<SupportRequestTypeKey, React.ReactElement> = {
  support: <LifeBuoyIcon size={14} strokeWidth={1.5} />,
  bug: <BugIcon size={14} strokeWidth={1.5} />,
  feature: <SparklesIcon size={14} strokeWidth={1.5} />,
}

/** Every type, in intake order — the option list for the Type filter facet. */
export const SUPPORT_TYPE_VALUES = Object.keys(SUPPORT_REQUEST_TYPES) as SupportRequestTypeKey[]

interface SupportTypeBadgeProps {
  type: string
  /**
   * DENSITY only — never content, never colour.
   *
   * `small` matches the 28px status pill it sits beside on a request card; `medium` matches
   * the taller chips in the filter drawer, where every facet's options line up at one height
   * and a 28px badge would read as a different control. Same component, same glyph, same
   * tone: only the padding differs, which is precisely the split `StatusChip` already makes
   * with its own `size`.
   */
  size?: 'small' | 'medium'
  /**
   * `false` renders the drawer's UNSELECTED state — muted outline, no fill.
   *
   * Weight-neutral against the selected state: the border is 1px in BOTH, so toggling an
   * option never shifts the row it sits in. Selected simply paints that border in the fill
   * colour, which reads as a seamless tint rather than an outline.
   */
  selected?: boolean
}

export const SupportTypeBadge = ({
  type,
  size = 'small',
  selected = true,
}: SupportTypeBadgeProps) => {
  const theme = useTheme()
  const { resolvedMode } = useColorMode()
  const isDark = resolvedMode === 'dark'

  const config = SUPPORT_REQUEST_TYPES[type as SupportRequestTypeKey]
  if (!config) {
    console.error(`Invalid support request 'type': ${type}`)
    return null
  }

  /**
   * ALL THREE PARTS from `statusTone`, not just the text colour (VRG-332).
   *
   * This took `.color` and hand-rolled its own fill, and used that fill AS the border —
   * so the selected badge had, in effect, no visible edge, and read as a flat block
   * beside the bordered status pill it shares a row with on every request card. The pill,
   * the announcement kind badge and this now all derive text, border and fill from one
   * function, per mode, so none of the three can drift from the other two.
   *
   * WHY THE FILL CHANGED, since it is a real (if small) visual delta: it was
   * `alpha(hue, isDark ? .16 : .1)`, a second formula that happened to sit near
   * `statusTone`'s `.18 / .14`. Two nearly-identical formulas for one thing is the exact
   * shape of drift this epic closed on the announcement side, and keeping it here would
   * mean a `feature` REQUEST and a `feature` ANNOUNCEMENT — deliberately the same hue and
   * the same glyph, because they are one thing at two moments — were tinted two different
   * strengths.
   *
   * THE UNSELECTED STATE IS UNTOUCHED: grey outline, transparent fill. That is what
   * carries the toggle's on/off reading in the filter drawer, and it is untouched by the
   * selected state gaining a tinted edge — fill-versus-no-fill is still the primary
   * signal, and the border stays 1px in both, so toggling still shifts nothing.
   */
  const {
    color: toneColor,
    border: toneBorder,
    background: fill,
  } = statusTone(config, isDark)
  const isMedium = size === 'medium'

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        flexShrink: 0,
        width: 'fit-content',
        px: isMedium ? 2.5 : 2,
        borderRadius: 1.5,
        minHeight: isMedium ? 38 : STATUS_TOKEN_H,
        border: '1px solid',
        borderColor: selected ? toneBorder : theme.palette.grey[300],
        backgroundColor: selected ? fill : 'transparent',
        color: selected ? toneColor : theme.palette.text.primary,
        transition: 'background-color .12s, border-color .12s, color .12s',
        '& svg': { color: selected ? toneColor : theme.palette.text.secondary },
      }}
    >
      {TYPE_ICON[type as SupportRequestTypeKey]}
      {/*
        `small` is 12/500 — the STATUS PILL's type (`StatusChip.tsx`: 12/500 at
        `size="small"`), matching the announcement kind badge for the same reason the
        border and fill now match: a `feature` REQUEST and a `feature` ANNOUNCEMENT are
        one thing at two moments, so letting one drop to 12 while the other stayed at
        14/600 would re-open the gap on the axis it was just closed on.

        `medium` (the filter drawer) keeps 14: those chips are a taller, standalone
        control sized to line up with the other facets' options, not a token sitting
        beside a 28px pill in a table row.
      */}
      <Typography
        sx={{
          fontWeight: isMedium ? 600 : 500,
          lineHeight: 1,
          fontSize: isMedium ? 14 : 12,
        }}
      >
        {config.label}
      </Typography>
    </Stack>
  )
}
